import { BROKEN_IMAGE_SRC } from "@remi/constants";
import { splitContactName } from "@remi/formatting";
import { RemiAvatar, RemiAvatarGroup, RemiTooltip, RemiTooltipContent, RemiTooltipTrigger } from "@remi/next-ui";

import { useFilesByIds } from "~/hooks/files/useFile";
import { usePersonProfilePhoto, usePersonProfilePhotos } from "~/hooks/usePerson";
import { cn } from "~/lib/utils";
import { GroupTooltipContent } from "./GroupTooltipContent";
import { PersonTooltipContent } from "./PersonTooltipContent";

const FOCUSABLE_AVATAR_CLASS =
  "rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-border-accent";

interface SingleAvatarPerson {
  id: string;
  contact: { name: string };
  profilePhotoId: string | null;
}

interface SingleAvatarProps {
  personId?: string;
  person?: SingleAvatarPerson;
  focusable?: boolean;
  tooltipContent?: React.FC<{ personId: string }> | null;
  size?: "xxs" | "xs" | "sm" | "md" | "lg" | "xlg" | "xl" | "xxl";
  active?: boolean;
  icon?: React.ReactNode;
  border?: boolean;
  className?: string;
  showPersonName?: boolean;
  showFirstNameOnly?: boolean;
}

type AvatarViewPerson =
  | (SingleAvatarPerson & { profilePhotoUrl: string })
  | NonNullable<ReturnType<typeof usePersonProfilePhoto>["data"]>;

type AvatarViewProps = Omit<SingleAvatarProps, "personId" | "person"> & { person: AvatarViewPerson | undefined };

export const SingleAvatar = ({ personId, person, ...props }: SingleAvatarProps) =>
  person ? <LoadedPersonAvatar person={person} {...props} /> : <FetchedPersonAvatar personId={personId} {...props} />;

const FetchedPersonAvatar = ({ personId, ...props }: Omit<SingleAvatarProps, "person">) => {
  const { data: person } = usePersonProfilePhoto(personId);
  return <AvatarView person={person} {...props} />;
};

interface LoadedPersonAvatarProps extends Omit<SingleAvatarProps, "personId" | "person"> {
  person: SingleAvatarPerson;
}

const LoadedPersonAvatar = ({ person, ...props }: LoadedPersonAvatarProps) => {
  const { data: photos } = useFilesByIds(person.profilePhotoId ? [person.profilePhotoId] : []);
  const photo = photos[0];
  return (
    <AvatarView
      person={{ ...person, profilePhotoUrl: photo?.thumbnailUrl ?? photo?.url ?? BROKEN_IMAGE_SRC }}
      {...props}
    />
  );
};

const AvatarView = ({
  person,
  focusable = true,
  tooltipContent: TooltipContent = PersonTooltipContent,
  size = "sm",
  active = false,
  icon,
  border,
  showPersonName = false,
  showFirstNameOnly = false,
  className,
}: AvatarViewProps) => {
  if (!person) {
    return <RemiAvatar size={size} person={undefined} border={border} className={className} />;
  }

  if (TooltipContent) {
    return (
      <RemiTooltip>
        <RemiTooltipTrigger asChild className="h-fit w-fit">
          <div
            data-testid="single-avatar"
            // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focus target for a tooltip or overflow, not a control
            tabIndex={focusable ? 0 : undefined}
            className={cn("flex items-center gap-1", focusable && FOCUSABLE_AVATAR_CLASS, className)}
          >
            <RemiAvatar
              size={size}
              person={person}
              icon={icon}
              url={person.profilePhotoUrl}
              status={active ? "active" : undefined}
              border={border}
            />
            {showPersonName && !showFirstNameOnly && <div>{person.contact.name}</div>}
            {showFirstNameOnly && <div>{splitContactName(person.contact.name)[0]}</div>}
          </div>
        </RemiTooltipTrigger>
        <RemiTooltipContent className="h-fit w-fit max-w-none p-0" side="bottom" align="start">
          <TooltipContent personId={person.id} />
        </RemiTooltipContent>
      </RemiTooltip>
    );
  }

  return (
    <div data-testid="single-avatar" className={cn("flex items-center gap-1", className)}>
      <RemiAvatar
        size={size}
        person={person}
        icon={icon}
        url={person.profilePhotoUrl}
        status={active ? "active" : undefined}
        border={border}
      />
      {showPersonName && !showFirstNameOnly && <div>{person.contact.name}</div>}
      {showFirstNameOnly && <div>{splitContactName(person.contact.name)[0]}</div>}
    </div>
  );
};

interface GroupAvatarProps {
  personIds: string[];
  tooltipContent?: React.FC<{ personIds: string[] }> | null;
  size?: "xs" | "sm" | "md" | "lg" | "xlg" | "xl" | "xxl";
  active?: boolean;
  border?: boolean;
  className?: string;
  overflow?: number;
}

export const GroupAvatar = ({
  personIds,
  tooltipContent: TooltipContent = GroupTooltipContent,
  size = "sm",
  active,
  border,
  className,
  overflow,
}: GroupAvatarProps) => {
  const { data: persons } = usePersonProfilePhotos(personIds);

  const body = (
    <RemiAvatarGroup
      size={size}
      overflow={overflow}
      persons={persons.map((p) => ({
        ...p,
        status: active ? "active" : undefined,
        url: p.profilePhotoUrl,
      }))}
      border={border}
      className={className}
    />
  );

  if (personIds.length > 0 && TooltipContent) {
    return (
      <RemiTooltip>
        <RemiTooltipTrigger asChild className="h-fit w-fit">
          {body}
        </RemiTooltipTrigger>
        <RemiTooltipContent className="h-fit w-fit max-w-none p-0" side="bottom" align="start">
          <TooltipContent personIds={personIds} />
        </RemiTooltipContent>
      </RemiTooltip>
    );
  }

  return body;
};
